import { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import apiService from '../../../services/apiService';
import InvoiceModal from './InvoiceModal';
import useApi from '../../../hooks/useApi';

const UserSubscriptions = () => {
  const { user } = useContext(AuthContext);
  const { data: userData } = useApi(`/users/${user?.id || ''}`);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showInvoice, setShowInvoice] = useState(false);

  const DownloadIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );

  const fetchSubscription = async () => {
  if (!user?.id) {
    setError('Please log in to view your subscription.');
    setIsLoading(false);
    return;
  }
  setIsLoading(true);
  try {
    const res = await apiService.get(`/stripe/subscription-status?userId=${user.id}`);

    let subscriptionHistory = res.data.subscriptionHistory || [];

    // If history is empty but there's an expired subscriptionStatus, synthesize it
    if (
      subscriptionHistory.length === 0 &&
      !res.data.active &&
      res.data.plan &&
      res.data.startDate
    ) {
      subscriptionHistory = [{
        _id: 'synthetic-expired',
        plan: res.data.plan,
        planId: res.data.planId || null,
        platform: res.data.platform || null,
        startDate: res.data.startDate,
        endDate: res.data.endDate,
        status: 'expired',
        amount: res.data.price || 0,
        cancelledAt: null,
      }];
    }

    setSubscription({
      ...res.data,
      plan: res.data.plan || 'monthly',
      name: res.data.name || 'Unknown Plan',
      price: res.data.price || 0,
      platform: res.data.platform || 'N/A',
      startDate: res.data.startDate || null,
      endDate: res.data.endDate || null,
      description: res.data.description || '',
      subscriptionHistory,
    });
    setError('');
  } catch (err) {
    setError('Could not load subscription details. Please try again.');
    console.error(err);
  } finally {
    setIsLoading(false);
  }
};

  useEffect(() => {
    fetchSubscription();
  }, [user]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const formatInterval = (interval) => {
    if (!interval) return 'Unknown';
    const map = {
      monthly: 'Monthly',
      yearly: 'Yearly',
      '2years': '2 Years',
      '3years': '3 Years',
    };
    return map[interval] || interval.charAt(0).toUpperCase() + interval.slice(1);
  };

  const getBenefitsList = (html) => {
    if (!html) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const items = doc.querySelectorAll('li');
    return Array.from(items).map((li) => li.textContent.trim()).filter(Boolean);
  };

  // Helper to calculate VAT breakdown (VAT-inclusive price)
  const getVatBreakdown = (total) => {
    if (!total && total !== 0) return { total: '—', exclVat: '—', vat: '—' };
    const vatRate = 0.20;
    const exclVat = total / (1 + vatRate);
    const vat = total - exclVat;
    return {
      total: Number(total).toFixed(2),
      exclVat: exclVat.toFixed(2),
      vat: vat.toFixed(2),
    };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center p-6">
        <div className="flex flex-col items-center space-y-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
          <p className="text-gray-500 font-medium">Loading your subscription...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Oops...</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchSubscription}
            className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const benefits = getBenefitsList(subscription?.description || '');
  const introText = subscription?.description?.split(/<ol>|<ul>/i)[0] || '';
  const vatInfo = getVatBreakdown(subscription?.price);

  return (
    <div className="min-h-screen bg-gray-50/40 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium mb-4">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Active Subscription
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            Your Subscription
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 xl:gap-8">
          {/* Plan Overview Card */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 sm:p-8">
              {subscription?.plan ? (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{subscription.name}</h2>
                    <p className="text-gray-500 mt-1">
                      {formatInterval(subscription.plan)} • {subscription.platform}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-baseline gap-2 justify-end">
                      <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                        £{vatInfo.total}
                      </span>
                      <span className="text-sm font-medium text-gray-500">incl. VAT</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      Excl. VAT: £{vatInfo.exclVat} • VAT: £{vatInfo.vat}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-600 text-lg">No active plan found.</p>
              )}

              {/* Plan intro text */}
              {introText && (
                <div
                  className="prose prose-gray max-w-none mb-8 text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: introText }}
                />
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-5 text-center border border-gray-100">
                  <p className="text-sm text-gray-500 mb-1">Started</p>
                  <p className="text-xl font-semibold text-gray-900">{formatDate(subscription?.startDate)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-5 text-center border border-gray-100">
                  <p className="text-sm text-gray-500 mb-1">Expires</p>
                  <p className="text-xl font-semibold text-gray-900">{formatDate(subscription?.endDate)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-5">What's Included</h3>
            {benefits.length > 0 ? (
              <ul className="space-y-3">
                {benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-indigo-500" />
                    <span className="text-gray-700 text-sm leading-6">{benefit}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">No benefits listed for this plan.</p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 lg:sticky lg:top-8 h-fit">
            <h3 className="text-lg font-semibold text-gray-900 mb-5 text-center sm:text-left">Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => setShowInvoice(true)}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition shadow-sm"
              >
                <DownloadIcon className="w-5 h-5" />
                Download Invoice
              </button>

              <a
                href="/subscriptions"
                className="block w-full text-center px-5 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition shadow-sm"
              >
                Change / Upgrade Plan
              </a>

              <button className="w-full px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition border border-gray-200">
                Manage Payment Method
              </button>
            </div>
          </div>
        </div>

        {/* Subscription History */}
        {subscription?.subscriptionHistory?.length > 0 && (
          <div className="mt-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-5">Subscription History</h2>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Started</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ended</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {subscription.subscriptionHistory
                      .slice()
                      .reverse()
                      .map((item, idx) => {
                        const histVat = getVatBreakdown(item.amount);
                        return (
                          <tr
                            key={item._id || idx}
                            className="hover:bg-gray-50/70 transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{formatInterval(item.plan)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-900">
                                  £{histVat.total}
                                </span>
                                <span className="text-xs text-gray-500">incl. VAT</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {formatDate(item.startDate)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {formatDate(item.endDate)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${
                                  item.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : item.status === 'upgraded'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {item.status
                                  ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
                                  : 'Unknown'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {showInvoice && (
        <InvoiceModal
          subscription={subscription}
          userData={userData}
          onClose={() => setShowInvoice(false)}
        />
      )}
    </div>
  );
};

export default UserSubscriptions;